import Vue from 'vue';
import VueNativeSock from 'vue-native-websocket';

import store from '@/store';
import type { PriceSocket } from '@/types/priceSocket';

declare const SEO_API_WS_URL: string;

type Resolvable = string | (() => string);

interface PriceSocketApi {
  $connect: (url?: string) => void;
  $disconnect: () => void;
  $connectToPrice: (market: string, fiat: string) => void;
}

const resolve = (value: Resolvable): string => (typeof value === 'function' ? value() : value);

const buildUrl = (marketName: string, fiatCode: string): string =>
  `${SEO_API_WS_URL}?market=${marketName}&fiat=${fiatCode}`;

const socketApi = (): PriceSocketApi => Vue.prototype as unknown as PriceSocketApi;

// XXX: Vue.use() dedupes by plugin identity, so VueNativeSock installs only once per page.
// The mutations map and $connect/$disconnect/$socket therefore belong to the FIRST caller.
// No page currently mounts two callers at once; fixing this properly means dropping
// vue-native-websocket for per-connection instances (see member-spa/plugins/cryptoPriceWS.ts).
const cryptoPriceWS = (
  mutationName: string,
  marketName: Resolvable,
  fiatCode: Resolvable,
): PriceSocket => {
  Vue.use(VueNativeSock, buildUrl(resolve(marketName), resolve(fiatCode)), {
    connectManually: true,
    store,
    mutations: {
      SOCKET_ONOPEN: `${mutationName}/SOCKET_ONOPEN`,
      SOCKET_ONCLOSE: `${mutationName}/SOCKET_ONCLOSE`,
      SOCKET_ONERROR: `${mutationName}/SOCKET_ONERROR`,
      SOCKET_ONMESSAGE: `${mutationName}/SOCKET_ONMESSAGE`,
      SOCKET_RECONNECT: `${mutationName}/SOCKET_RECONNECT`,
    },
    format: 'json',
    reconnection: true,
    reconnectionAttempts: 3,
  });

  socketApi().$connectToPrice = (market: string, fiat: string): void => {
    socketApi().$connect(buildUrl(market, fiat));
  };

  return {
    // The fiat is carried only by the connection URL, and callers defer connect() by 15s.
    // Resolve market/fiat when connect() actually fires, never at registration time.
    connect: (): void => {
      socketApi().$connect(buildUrl(resolve(marketName), resolve(fiatCode)));
    },
    disconnect: (): void => {
      socketApi().$disconnect();
    },
    connectToPrice: (market: string, fiat: string): void => {
      socketApi().$connectToPrice(market, fiat);
    },
  };
};

export default cryptoPriceWS;
